import { ITooltipParams } from "ag-grid-community"
import { useMemo } from "react"
import {
  PeriodLine,
  TOOLTIP_COLORS,
  TooltipContainer,
  TooltipPeriodSection,
  TooltipTable,
  TooltipTd,
  TooltipTh,
} from "./TableTooltip.styled"

export interface TooltipMetric {
  label: string
  baseline: string | null
  adjusted: string | null
  baseline3p: string | null
  adjusted3p: string | null
  baseline9p: string | null
  adjusted9p: string | null
}

export interface TooltipData {
  metrics: TooltipMetric[]
}

const PERIOD_LABELS = {
  START: "Start Date",
  END: "End Date",
} as const

const TableTooltip = (props: ITooltipParams) => {
  const data = useMemo<TooltipData | null>(() => {
    if (!props.value) return null
    try {
      return JSON.parse(props.value) as TooltipData
    } catch {
      return null
    }
  }, [props.value])

  if (!data || data.metrics.length === 0) return null

  const metricRows = data.metrics.filter(
    m => m.label !== PERIOD_LABELS.START && m.label !== PERIOD_LABELS.END,
  )

  const startRow = data.metrics.find(m => m.label === PERIOD_LABELS.START)
  const endRow = data.metrics.find(m => m.label === PERIOD_LABELS.END)

  const showBaseline = data.metrics.some(m => m.baseline != null)
  const showAdjusted = data.metrics.some(m => m.adjusted != null)
  const showBaseline3p = data.metrics.some(m => m.baseline3p != null)
  const showAdjusted3p = data.metrics.some(m => m.adjusted3p != null)
  const showBaseline9p = data.metrics.some(m => m.baseline9p != null)
  const showAdjusted9p = data.metrics.some(m => m.adjusted9p != null)

  const hasPeriodInfo =
    (startRow?.baseline && endRow?.baseline) ||
    (startRow?.baseline3p && endRow?.baseline3p) ||
    (startRow?.baseline9p && endRow?.baseline9p)

  return (
    <TooltipContainer minWidth={520}>
      <TooltipTable>
        <thead>
          <tr>
            <TooltipTh>Metric</TooltipTh>
            {showBaseline && (
              <TooltipTh color={TOOLTIP_COLORS.BASELINE_1P} align="right">
                Baseline (T+1)
              </TooltipTh>
            )}
            {showAdjusted && (
              <TooltipTh color={TOOLTIP_COLORS.ADJUSTED_1P} align="right">
                Adjusted (T+1)
              </TooltipTh>
            )}
            {showBaseline3p && (
              <TooltipTh color={TOOLTIP_COLORS.BASELINE_3P} align="right">
                Baseline (T+3)
              </TooltipTh>
            )}
            {showAdjusted3p && (
              <TooltipTh color={TOOLTIP_COLORS.ADJUSTED_3P} align="right">
                Adjusted (T+3)
              </TooltipTh>
            )}
            {showBaseline9p && (
              <TooltipTh color={TOOLTIP_COLORS.BASELINE_9P} align="right">
                Baseline (T+9)
              </TooltipTh>
            )}
            {showAdjusted9p && (
              <TooltipTh color={TOOLTIP_COLORS.ADJUSTED_9P} align="right">
                Adjusted (T+9)
              </TooltipTh>
            )}
          </tr>
        </thead>

        <tbody>
          {metricRows.map((row, index) => (
            <tr key={index}>
              <TooltipTd>{row.label}</TooltipTd>
              {showBaseline && (
                <TooltipTd color={TOOLTIP_COLORS.BASELINE_1P} align="right">
                  {row.baseline ?? "-"}
                </TooltipTd>
              )}
              {showAdjusted && (
                <TooltipTd color={TOOLTIP_COLORS.ADJUSTED_1P} align="right">
                  {row.adjusted ?? "-"}
                </TooltipTd>
              )}
              {showBaseline3p && (
                <TooltipTd color={TOOLTIP_COLORS.BASELINE_3P} align="right">
                  {row.baseline3p ?? "-"}
                </TooltipTd>
              )}
              {showAdjusted3p && (
                <TooltipTd color={TOOLTIP_COLORS.ADJUSTED_3P} align="right">
                  {row.adjusted3p ?? "-"}
                </TooltipTd>
              )}
              {showBaseline9p && (
                <TooltipTd color={TOOLTIP_COLORS.BASELINE_9P} align="right">
                  {row.baseline9p ?? "-"}
                </TooltipTd>
              )}
              {showAdjusted9p && (
                <TooltipTd color={TOOLTIP_COLORS.ADJUSTED_9P} align="right">
                  {row.adjusted9p ?? "-"}
                </TooltipTd>
              )}
            </tr>
          ))}
        </tbody>
      </TooltipTable>

      {hasPeriodInfo && (
        <TooltipPeriodSection>
          {startRow?.baseline && endRow?.baseline && (
            <PeriodLine color={TOOLTIP_COLORS.BASELINE_1P}>
              For horizon <strong>T+1</strong> --{">"} Backtesting between {startRow.baseline} and{" "}
              {endRow.baseline}
            </PeriodLine>
          )}

          {startRow?.baseline3p && endRow?.baseline3p && (
            <PeriodLine color={TOOLTIP_COLORS.BASELINE_3P}>
              For horizon <strong>T+3</strong> --{">"} Backtesting between {startRow.baseline3p} and{" "}
              {endRow.baseline3p}
            </PeriodLine>
          )}

          {startRow?.baseline9p && endRow?.baseline9p && (
            <PeriodLine color={TOOLTIP_COLORS.BASELINE_9P}>
              For horizon <strong>T+9</strong> --{">"} Backtesting between {startRow.baseline9p} and{" "}
              {endRow.baseline9p}
            </PeriodLine>
          )}
        </TooltipPeriodSection>
      )}
    </TooltipContainer>
  )
}

export default TableTooltip
